import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import _orderBy from "lodash/orderBy";

import { buildUserIsOnlineMap } from "shared/lib/helpers/chat";
import { TIME_INTERVALS } from "shared/lib/constants/time";

import { useApiClient } from "@/context/api-client";
import { getChatIdentity } from "@/modules/messages/utils";

import { MessagingQueryKeys } from "./keys";

export const useQueryFetchChats = (searchTerm: string | undefined) => {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: MessagingQueryKeys.chats(searchTerm),
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.messaging.getChatInboxV2({
        search_term: searchTerm,
        next_token: pageParam,
      });

      return response.data;
    },
    initialPageParam: undefined as string | null | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_token || undefined,
    select: (data) =>
      data
        ? _orderBy(
            data.pages.flatMap((page) => page.chat_inbox || []),
            "last_message_timestamp",
            "desc"
          )
        : [],
  });
};

export const useQueryFetchContactsToMessage = () => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: MessagingQueryKeys.contacts(),
    queryFn: async () => {
      const response = await apiClient.messaging.getCustomersToChatWith();

      return response.data;
    },
    select: (data) => data.campaign_customers,
  });
};

export const useQueryFetchMessages = (
  campaignId: string | undefined,
  customerId: string | undefined
) => {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    enabled: !!campaignId && !!customerId,
    queryKey: MessagingQueryKeys.messages(
      getChatIdentity({
        campaign_id: campaignId as string,
        customer: {
          id: customerId as string,
        },
      })
    ),
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.messaging.getMessagingChatHistoryV2(
        campaignId as string,
        customerId as string,
        pageParam
      );

      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: TIME_INTERVALS.ONE_MINUTE * 5,
    initialPageParam: undefined as string | null | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_token || undefined,
    select: (data) => data?.pages.flatMap((page) => page.chat_history || []),
  });
};

export const useQueryFetchOnlineStatuses = (
  customerIds: string[] | undefined
) => {
  const apiClient = useApiClient();

  return useQuery({
    enabled: !!customerIds && customerIds.length > 0,
    queryKey: MessagingQueryKeys.onlineStatuses(customerIds as string[]),
    queryFn: () =>
      apiClient.messaging.getChatInboxOnlineStatuses(customerIds as string[]),
    refetchInterval: TIME_INTERVALS.ONE_SECOND * 5,
    select: (response) => buildUserIsOnlineMap(response?.data?.online_statuses),
  });
};

export const useQueryFetchCustomerToChatOnAccountDetails = (
  campaignId: string | undefined,
  accountId: string | undefined
) => {
  const apiClient = useApiClient();

  return useQuery({
    enabled: !!campaignId && !!accountId,
    queryKey: MessagingQueryKeys.customerToChatOnAccountDetails(
      campaignId as string,
      accountId as string
    ),
    queryFn: () =>
      apiClient.messaging.getCustomerToChatOnAccountDetails(
        campaignId as string,
        accountId as string
      ),
    select: (response) => response.data.customer_id,
  });
};
